import React, { useCallback, useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Film,
  Plus,
  CheckCircle,
  AlertTriangle,
  Clock,
  Sparkles,
  Layers,
  X,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Trash2,
} from "lucide-react";

const brl = (n) =>
  `R$ ${Number(n || 0)
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;

const roiLabel = (roi) => (roi === null || roi === undefined ? "—" : `${roi.toFixed(2)}x`);

export default function CreativesPage() {
  const { user } = useAuth();
  const [creatives, setCreatives] = useState([]);
  const [insights, setInsights] = useState({ best: null, worst: null });
  const [bms, setBms] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Form State
  const [name, setName] = useState("");
  const [product, setProduct] = useState("");
  const [angle, setAngle] = useState("");
  const [format, setFormat] = useState("Vídeo UGC");
  const [status, setStatus] = useState("ready");
  const [refUrl, setRefUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [publicationStatus, setPublicationStatus] = useState("paused");
  const [bmId, setBmId] = useState("");
  const [adAccountId, setAdAccountId] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Publication toggle state (per creative card)
  const [publishingId, setPublishingId] = useState(null);
  const [pubBmId, setPubBmId] = useState("");
  const [pubAccountId, setPubAccountId] = useState("");
  const [pubError, setPubError] = useState("");
  const [pubBusy, setPubBusy] = useState(false);

  const fetchCreatives = useCallback(async () => {
    try {
      const [listRes, insightsRes, bmsRes, aaRes] = await Promise.all([
        api.get("/api/creatives/"),
        api.get("/api/creatives/insights"),
        api.get("/api/bms"),
        api.get("/api/ad-accounts"),
      ]);
      setCreatives(listRes.data.creatives || []);
      setInsights(insightsRes.data || { best: null, worst: null });
      setBms(bmsRes.data?.bms || []);
      setAdAccounts(aaRes.data?.ad_accounts || []);
    } catch (err) {
      console.error("Creatives fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchCreatives();
  }, [user, fetchCreatives]);

  const accountsForBm = (bid) => adAccounts.filter((a) => a.bm_id === bid);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    if (publicationStatus === "published" && (!bmId || !adAccountId)) {
      setFormError("Para publicar o criativo informe a BM e a Conta de Anúncios.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/creatives/", {
        name,
        product,
        angle,
        format,
        status,
        external_reference_url: refUrl || undefined,
        notes: notes || undefined,
        publication_status: publicationStatus,
        active_bm_id: publicationStatus === "published" ? bmId : undefined,
        active_ad_account_id: publicationStatus === "published" ? adAccountId : undefined,
      });
      setShowModal(false);
      setName("");
      setProduct("");
      setAngle("");
      setRefUrl("");
      setNotes("");
      setPublicationStatus("paused");
      setBmId("");
      setAdAccountId("");
      fetchCreatives();
    } catch (err) {
      setFormError(err?.response?.data?.detail || "Erro ao cadastrar criativo.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (creativeId, newStatus) => {
    try {
      await api.patch(`/api/creatives/${creativeId}/status`, { status: newStatus });
      fetchCreatives();
    } catch (err) {
      alert("Erro ao alterar status.");
    }
  };

  const openPublishPicker = (c) => {
    setPublishingId(c.id || c._id);
    setPubBmId(c.active_bm_id || "");
    setPubAccountId(c.active_ad_account_id || "");
    setPubError("");
  };

  const confirmPublish = async () => {
    setPubError("");
    if (!pubBmId || !pubAccountId) {
      setPubError("Selecione BM e Conta de Anúncios.");
      return;
    }
    setPubBusy(true);
    try {
      await api.patch(`/api/creatives/${publishingId}/publication`, {
        publication_status: "published",
        bm_id: pubBmId,
        ad_account_id: pubAccountId,
      });
      setPublishingId(null);
      fetchCreatives();
    } catch (err) {
      setPubError(err?.response?.data?.detail || "Erro ao publicar criativo.");
    } finally {
      setPubBusy(false);
    }
  };

  const togglePause = async (c) => {
    const id = c.id || c._id;
    try {
      await api.patch(`/api/creatives/${id}/publication`, {
        publication_status: "paused",
      });
      fetchCreatives();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao pausar criativo.");
    }
  };

  const handleDeleteCreative = async (c) => {
    const id = c.id || c._id;
    const label = c.name || "este criativo";
    if (!window.confirm(
      `Excluir "${label}"? O histórico de leads, vendas e gastos permanece preservado para relatórios de ciclos anteriores, mas o criativo some do inventário ativo.`
    )) return;
    try {
      await api.delete(`/api/creatives/${id}`);
      fetchCreatives();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao excluir criativo.");
    }
  };

  const readyOrScaling = creatives.filter((c) => ["ready", "validated", "scaling"].includes(c.status));
  const reserveCount = creatives.filter((c) => c.status === "ready").length;

  const statusLabels = {
    idea: { label: "Ideia", color: "bg-gray-700 text-gray-300" },
    in_production: { label: "Em Produção", color: "bg-blue-500/20 text-blue-400 border border-blue-500/30" },
    ready: { label: "Pronto (Reserva)", color: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" },
    in_test: { label: "Em Teste", color: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30" },
    validated: { label: "Validado 🔥", color: "bg-[#FF6B00]/20 text-[#FF6B00] border border-[#FF6B00]/30 font-bold" },
    scaling: { label: "Escalando 🚀", color: "bg-emerald-500/30 text-emerald-300 border border-emerald-500 font-extrabold" },
    discarded: { label: "Descartado", color: "bg-red-500/20 text-red-400 border border-red-500/30" },
  };

  return (
    <div data-testid="creatives-page-container" className="space-y-6 max-w-5xl mx-auto px-4 py-4 pb-20">
      {/* Header */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-[#FF6B00] uppercase tracking-wider mb-1">
            <Film className="w-4 h-4" />
            <span>Inventário de Criativos</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-white">Registro de Criativos de Anúncio</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Gerenciamento operacional de criativos testados, validados e reservas para sua operação.
          </p>
        </div>

        <button
          data-testid="open-create-creative-btn"
          onClick={() => setShowModal(true)}
          className="px-4 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(255,107,0,0.4)] flex items-center gap-1.5 self-start sm:self-auto"
        >
          <Plus className="w-4 h-4 stroke-[3]" />
          <span>Cadastrar Criativo</span>
        </button>
      </div>

      {/* Creative Reserve Plan B Alert */}
      {reserveCount === 0 ? (
        <div
          data-testid="creative-reserve-warning"
          className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 flex items-start gap-3 text-xs text-amber-200"
        >
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <span className="font-bold text-amber-300 block text-sm">⚠️ Criativos reserva: 0 — Sua operação está sem plano B</span>
            <span>
              Tenha sempre pelo menos 1 criativo no status "Pronto" como reserva para garantir a saúde da operação durante a Pré-Escala e Escala.
            </span>
          </div>
        </div>
      ) : (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center justify-between text-xs text-emerald-300">
          <span className="font-semibold flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span>Plano B ativo: {reserveCount} criativo(s) pronto(s) de reserva.</span>
          </span>
          <span className="font-mono text-[11px] bg-emerald-500/20 px-2 py-0.5 rounded text-emerald-300">
            Reserva OK
          </span>
        </div>
      )}

      {/* Attribution Insights — deterministic-only, one positive + one negative */}
      {(insights?.best || insights?.worst) && (
        <div
          data-testid="creative-insights-panel"
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          {insights?.best && (
            <div
              data-testid="creative-insight-best"
              className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4"
            >
              <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-emerald-400 mb-1">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>Melhor Contribuidor</span>
              </div>
              <div className="text-sm font-extrabold text-white leading-snug">
                {insights.best.name}
              </div>
              <p className="text-xs text-emerald-200/80 mt-1 leading-relaxed">
                Este é o criativo que mais está contribuindo para a saúde da sua operação.
              </p>
              <div className="mt-2 flex gap-3 text-[11px] text-gray-300">
                <span>
                  Vendas:{" "}
                  <span className="font-bold text-white">
                    {insights.best.attributed_sales_count}
                  </span>
                </span>
                <span>
                  Receita:{" "}
                  <span className="font-bold text-white">
                    {brl(insights.best.attributed_revenue)}
                  </span>
                </span>
                <span>
                  ROI:{" "}
                  <span className="font-bold text-emerald-300">
                    {roiLabel(insights.best.roi)}
                  </span>
                </span>
              </div>
            </div>
          )}
          {insights?.worst && (
            <div
              data-testid="creative-insight-worst"
              className="bg-red-500/10 border border-red-500/40 rounded-xl p-4"
            >
              <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-red-400 mb-1">
                <TrendingDown className="w-3.5 h-3.5" />
                <span>Atenção — Orçamento em Risco</span>
              </div>
              <div className="text-sm font-extrabold text-white leading-snug">
                {insights.worst.name}
              </div>
              <p className="text-xs text-red-200/80 mt-1 leading-relaxed">
                Este criativo está consumindo orçamento sem gerar receita.
              </p>
              <div className="mt-2 flex gap-3 text-[11px] text-gray-300">
                <span>
                  Gasto:{" "}
                  <span className="font-bold text-red-300">
                    {brl(insights.worst.attributed_spend)}
                  </span>
                </span>
                <span>
                  Vendas:{" "}
                  <span className="font-bold text-white">
                    {insights.worst.attributed_sales_count}
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Creatives List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {creatives.map((c) => {
          const stgInfo = statusLabels[c.status] || { label: c.status, color: "bg-gray-800 text-gray-300" };
          return (
            <div
              key={c.id || c._id}
              data-testid={`creative-card-${c.id || c._id}`}
              className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 flex flex-col justify-between hover:border-[#3E4760] transition-colors"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${stgInfo.color}`}>
                    {stgInfo.label}
                  </span>
                  <span className="text-[11px] text-gray-400">{c.format}</span>
                </div>

                <h3 className="text-base font-bold text-white mb-1 leading-snug">{c.name}</h3>
                <div className="text-xs text-[#FF6B00] font-semibold mb-2">{c.product}</div>

                <div className="bg-[#181B24] p-2.5 rounded-lg border border-[#282D3D] space-y-1 text-xs text-gray-300 mb-3">
                  <div>
                    <span className="text-gray-400 text-[11px]">Ângulo: </span>
                    <span className="font-medium text-white">{c.angle}</span>
                  </div>
                  {c.notes && (
                    <div className="text-[11px] text-gray-400 italic">"{c.notes}"</div>
                  )}
                  {c.external_reference_url && (
                    <a
                      href={c.external_reference_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-[#FF6B00] hover:underline flex items-center gap-1 pt-1"
                    >
                      <span>Ver referência externa</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>

                {/* Attribution metrics — deterministic only */}
                <div
                  data-testid={`creative-attribution-${c.id || c._id}`}
                  className="grid grid-cols-3 gap-1.5 mb-3"
                >
                  <div className="bg-[#101319] rounded-md p-1.5 border border-[#232733]">
                    <div className="text-[9px] uppercase tracking-wider text-gray-500">
                      Vendas
                    </div>
                    <div className="text-xs font-extrabold text-white tabular-nums">
                      {c.attributed_sales_count ?? 0}
                    </div>
                  </div>
                  <div className="bg-[#101319] rounded-md p-1.5 border border-[#232733]">
                    <div className="text-[9px] uppercase tracking-wider text-gray-500">
                      Receita
                    </div>
                    <div className="text-xs font-extrabold text-emerald-300 tabular-nums">
                      {brl(c.attributed_revenue)}
                    </div>
                  </div>
                  <div className="bg-[#101319] rounded-md p-1.5 border border-[#232733]">
                    <div className="text-[9px] uppercase tracking-wider text-gray-500">
                      ROI
                    </div>
                    <div className="text-xs font-extrabold text-[#FF6B00] tabular-nums">
                      {c.attributed_spend > 0 && c.attributed_sales_count > 0
                        ? `${(c.attributed_revenue / c.attributed_spend).toFixed(2)}x`
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-[#232733] space-y-2">
                {/* Publication state */}
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    {c.publication_status === "published" ? (
                      <span
                        data-testid={`creative-pub-badge-${c.id || c._id}`}
                        className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-300 font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Publicado
                      </span>
                    ) : (
                      <span
                        data-testid={`creative-pub-badge-${c.id || c._id}`}
                        className="inline-flex items-center gap-1 bg-gray-700/50 text-gray-300 font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        Pausado
                      </span>
                    )}
                  </div>
                  {c.publication_status === "published" ? (
                    <button
                      data-testid={`creative-pause-btn-${c.id || c._id}`}
                      onClick={() => togglePause(c)}
                      className="px-2.5 py-1 rounded bg-[#1A1D26] border border-[#282D3D] hover:border-amber-400 text-amber-300 font-bold text-[10px] uppercase"
                    >
                      Pausar
                    </button>
                  ) : (
                    <button
                      data-testid={`creative-publish-btn-${c.id || c._id}`}
                      onClick={() => openPublishPicker(c)}
                      className="px-2.5 py-1 rounded bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 text-emerald-300 font-bold text-[10px] uppercase"
                    >
                      Publicar
                    </button>
                  )}
                </div>
                {/* BM + Conta vinculada (histórico quando pausado) */}
                {(c.active_bm_id || c.active_ad_account_id) && (
                  <div
                    data-testid={`creative-pub-link-${c.id || c._id}`}
                    className="text-[10px] text-gray-400 leading-tight"
                  >
                    <span className="text-gray-500">BM: </span>
                    <span className="text-gray-200 font-semibold">
                      {bms.find((b) => (b.id || b._id) === c.active_bm_id)?.name || "—"}
                    </span>
                    <span className="text-gray-500 ml-2">Conta: </span>
                    <span className="text-gray-200 font-semibold">
                      {adAccounts.find((a) => (a.id || a._id) === c.active_ad_account_id)?.name || "—"}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs">
                  <span className="text-[11px] text-gray-400">Alterar Status:</span>
                  <select
                    data-testid={`select-creative-status-${c.id || c._id}`}
                    value={c.status}
                    onChange={(e) => handleStatusChange(c.id || c._id, e.target.value)}
                    className="bg-[#1A1D26] border border-[#282D3D] text-xs text-gray-200 rounded px-2 py-1 focus:outline-none focus:border-[#FF6B00]"
                  >
                    <option value="ready">Pronto</option>
                    <option value="in_test">Em Teste</option>
                    <option value="validated">Validado</option>
                    <option value="scaling">Escalando</option>
                    <option value="discarded">Descartado</option>
                    <option value="in_production">Em Produção</option>
                    <option value="idea">Ideia</option>
                  </select>
                </div>

                <button
                  data-testid={`creative-delete-btn-${c.id || c._id}`}
                  onClick={() => handleDeleteCreative(c)}
                  className="w-full mt-1 py-1.5 rounded bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300 font-bold text-[11px] uppercase tracking-wider inline-flex items-center justify-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Excluir criativo
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Register Creative Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-md p-6 relative shadow-2xl text-left">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-lg font-bold text-white mb-1">Cadastrar Criativo</h2>
            <p className="text-xs text-gray-400 mb-4">
              Registre os dados operacionais do anúncio para rastreamento.
            </p>

            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Nome do Criativo *
                </label>
                <input
                  type="text"
                  data-testid="creative-name-input"
                  required
                  placeholder="Ex: CRIATIVO 01 — Dor Articular UGC"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Produto Físico *
                </label>
                <input
                  type="text"
                  data-testid="creative-product-input"
                  required
                  placeholder="Ex: AlívioMax Caps"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Ângulo / Gancho *
                </label>
                <input
                  type="text"
                  data-testid="creative-angle-input"
                  required
                  placeholder="Ex: Depoimento de cliente com dor no joelho"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Formato</label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                  >
                    <option value="Vídeo UGC">Vídeo UGC</option>
                    <option value="Imagem Estática">Imagem Estática</option>
                    <option value="Carrossel">Carrossel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                  >
                    <option value="ready">Pronto</option>
                    <option value="in_production">Em Produção</option>
                    <option value="idea">Ideia</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Link de Referência Externa (Google Drive / Notion)
                </label>
                <input
                  type="url"
                  placeholder="https://drive.google.com/..."
                  value={refUrl}
                  onChange={(e) => setRefUrl(e.target.value)}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">
                  Observações
                </label>
                <input
                  type="text"
                  placeholder="Notas adicionais sobre a gravação"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-[#FF6B00]"
                />
              </div>

              {/* Publication state — Publicado requer BM + Conta */}
              <div className="pt-2 border-t border-[#232733] space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-300">
                    Status de publicação
                  </label>
                  <div
                    data-testid="creative-pub-toggle"
                    className="inline-flex rounded-full bg-[#1A1D26] border border-[#282D3D] overflow-hidden text-[10px] font-extrabold uppercase"
                  >
                    <button
                      type="button"
                      data-testid="creative-pub-toggle-paused"
                      onClick={() => setPublicationStatus("paused")}
                      className={`px-3 py-1 ${publicationStatus === "paused" ? "bg-gray-700/60 text-gray-100" : "text-gray-400"}`}
                    >
                      Pausado
                    </button>
                    <button
                      type="button"
                      data-testid="creative-pub-toggle-published"
                      onClick={() => setPublicationStatus("published")}
                      className={`px-3 py-1 ${publicationStatus === "published" ? "bg-emerald-500/25 text-emerald-200" : "text-gray-400"}`}
                    >
                      Publicado
                    </button>
                  </div>
                </div>
                {publicationStatus === "published" && (
                  <div className="grid grid-cols-1 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">
                        BM (Business Manager) *
                      </label>
                      <select
                        data-testid="creative-form-bm"
                        value={bmId}
                        onChange={(e) => { setBmId(e.target.value); setAdAccountId(""); }}
                        required
                        className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-emerald-500"
                      >
                        <option value="">Selecione a BM...</option>
                        {bms.map((b) => (
                          <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">
                        Conta de Anúncios *
                      </label>
                      <select
                        data-testid="creative-form-account"
                        value={adAccountId}
                        onChange={(e) => setAdAccountId(e.target.value)}
                        required
                        disabled={!bmId}
                        className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                      >
                        <option value="">Selecione a conta...</option>
                        {accountsForBm(bmId).map((a) => (
                          <option key={a.id || a._id} value={a.id || a._id}>{a.name}</option>
                        ))}
                      </select>
                      {bmId && accountsForBm(bmId).length === 0 && (
                        <p className="text-[10px] text-amber-400 mt-1">
                          Esta BM não possui contas cadastradas. Cadastre uma conta antes de publicar.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {formError && (
                <p data-testid="creative-form-error" className="text-xs text-red-400">{formError}</p>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 rounded-lg bg-[#1F2330] text-gray-300 font-bold text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="submit-creative-btn"
                  className="flex-1 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(255,107,0,0.3)]"
                >
                  {saving ? "Salvando..." : "Cadastrar Criativo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Publish picker — per creative */}
      {publishingId && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            data-testid="creative-publish-dialog"
            className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-sm p-6 relative shadow-2xl"
          >
            <button
              onClick={() => setPublishingId(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white p-1"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-base font-bold text-white mb-1">Publicar Criativo</h2>
            <p className="text-xs text-gray-400 mb-4">
              Vincule à BM e Conta de Anúncios onde ele está rodando. Gastos passam a ser atribuídos a essa conta.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">
                  BM *
                </label>
                <select
                  data-testid="creative-publish-bm"
                  value={pubBmId}
                  onChange={(e) => { setPubBmId(e.target.value); setPubAccountId(""); }}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-emerald-500"
                >
                  <option value="">Selecione a BM...</option>
                  {bms.map((b) => (
                    <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">
                  Conta de Anúncios *
                </label>
                <select
                  data-testid="creative-publish-account"
                  value={pubAccountId}
                  onChange={(e) => setPubAccountId(e.target.value)}
                  disabled={!pubBmId}
                  className="w-full bg-[#1A1D26] border border-[#282D3D] rounded-lg py-2 px-3 text-white text-xs focus:outline-none focus:border-emerald-500 disabled:opacity-50"
                >
                  <option value="">Selecione a conta...</option>
                  {accountsForBm(pubBmId).map((a) => (
                    <option key={a.id || a._id} value={a.id || a._id}>{a.name}</option>
                  ))}
                </select>
              </div>
              {pubError && (
                <p data-testid="creative-publish-error" className="text-xs text-red-400">{pubError}</p>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setPublishingId(null)}
                  className="flex-1 py-2.5 rounded-lg bg-[#1F2330] text-gray-300 font-bold text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  data-testid="creative-publish-confirm"
                  onClick={confirmPublish}
                  disabled={pubBusy}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-60"
                >
                  {pubBusy ? "Publicando..." : "Publicar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
